import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Zap, Crown, Shield, Check, Send, ArrowRight, Users, Mail, TrendingUp } from 'lucide-react';

const ICONS = { free: Zap, starter: Zap, professional: Shield, premium: Crown };
const COLORS = { free: 'border-border', starter: 'border-blue-300 bg-blue-50/30 dark:bg-blue-950/20', professional: 'border-purple-300 bg-purple-50/30 dark:bg-purple-950/20', premium: 'border-amber-300 bg-amber-50/30 dark:bg-amber-950/20' };
const BADGE_COLORS = { free:'bg-gray-100 text-gray-700', starter:'bg-blue-100 text-blue-700', professional:'bg-purple-100 text-purple-700', premium:'bg-amber-100 text-amber-700' };

export default function ConnectCredits() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [credits, setCredits] = useState(null);
  const [requests, setRequests] = useState({ sent:[], received:[] });
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState('');

  useEffect(() => {
    Promise.all([
      api.getConnectPackages(),
      api.getConnectCredits(),
      api.getConnectRequests()
    ]).then(([pkgs, cred, reqs]) => {
      setPackages(pkgs.packages||[]);
      setCredits(cred);
      setRequests(reqs);
    }).catch(() => toast.error('Failed to load'))
    .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (pkgId) => {
    if (pkgId === 'free') return toast.info("You're already on the free plan");
    setPurchasing(pkgId);
    try {
      const data = await api.subscribeConnectPackage(pkgId);
      if (data.url) window.location.href = data.url;
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed to start checkout'); }
    finally { setPurchasing(''); }
  };

  const currentPkg = credits?.package?.id || 'free';

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Smart Connect</h1>
          <p className="text-muted-foreground text-sm mt-1">Send cold outreach emails to professionals and grow your network</p>
        </div>

        {/* How it works */}
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="p-6">
            <h2 className="font-semibold mb-4">How Smart Connect Works</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { icon: Users, title: 'Browse Directory', desc: 'Find professionals you want to connect with in the Directory' },
                { icon: Mail, title: 'Send Connect Request', desc: 'Write a personal message — they receive a professional email on your behalf' },
                { icon: TrendingUp, title: 'Grow Your Network', desc: 'They visit your profile, accept the connection, and you both benefit' }
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <step.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{step.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Current plan stats */}
        {credits && (
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Plan</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-lg font-bold capitalize">{credits.package?.name || 'Free'}</p>
                    <Badge className={BADGE_COLORS[currentPkg]}>{credits.package?.name}</Badge>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Connect Credits</p>
                  <p className="text-2xl font-bold">
                    {credits.credits_per_month >= 999 ? '∞' : credits.credits_remaining}
                    {credits.credits_per_month < 999 && <span className="text-sm text-muted-foreground font-normal"> / {credits.credits_per_month}</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">remaining this month</p>
                </div>
              </div>
              {credits.credits_per_month < 999 && (
                <div className="mt-3">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{width:`${Math.min(100,(credits.credits_used/credits.credits_per_month)*100)}%`}} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{credits.credits_used} used · resets monthly</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Packages */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Plans</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map(pkg => {
              const Icon = ICONS[pkg.id] || Zap;
              const isCurrentPlan = currentPkg === pkg.id;
              return (
                <Card key={pkg.id} className={`relative transition-all ${COLORS[pkg.id]} ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`} data-testid={`pkg-${pkg.id}`}>
                  {pkg.id === 'professional' && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-purple-600 text-white text-xs">Most Popular</Badge></div>}
                  {isCurrentPlan && <div className="absolute -top-3 right-3"><Badge className="bg-green-600 text-white text-xs">Current Plan</Badge></div>}
                  <CardContent className="p-5">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${BADGE_COLORS[pkg.id]}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-bold text-base">{pkg.name}</h3>
                    <div className="flex items-baseline gap-1 mt-1 mb-4">
                      <span className="text-2xl font-bold">${pkg.price}</span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                    <ul className="space-y-2 mb-5">
                      {pkg.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />{f}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full" size="sm" variant={isCurrentPlan ? 'secondary' : pkg.id==='premium'?'default':'outline'}
                      disabled={isCurrentPlan || purchasing===pkg.id}
                      onClick={() => handleSubscribe(pkg.id)} data-testid={`subscribe-${pkg.id}`}>
                      {isCurrentPlan ? 'Current Plan' : purchasing===pkg.id ? 'Loading...' : pkg.price===0 ? 'Free' : <>Upgrade <ArrowRight className="w-3.5 h-3.5 ml-1"/></>}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Request History */}
        {(requests.sent?.length > 0 || requests.received?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Send className="w-4 h-4" />Sent Requests ({requests.sent?.length||0})</h3>
              <div className="space-y-2">
                {(requests.sent||[]).slice(0,5).map(r => (
                  <Card key={r.id}><CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{r.sender_name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{r.message}</p>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{r.status}</Badge>
                  </CardContent></Card>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Mail className="w-4 h-4" />Received Requests ({requests.received?.length||0})</h3>
              <div className="space-y-2">
                {(requests.received||[]).slice(0,5).map(r => (
                  <Card key={r.id}><CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{r.sender_name}</p>
                      <p className="text-xs text-muted-foreground">{r.sender_profession}</p>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{r.status}</Badge>
                  </CardContent></Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
